import * as React from 'react';
import {
  Box,
  IconButtonProps,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import DoNotStripSpacesSpan from '../../common/DoNotStripSpacesSpan';
import TrainingRevisionDelete from '../revisions/TrainingRevisionDelete';
import usePaginatedTrainingRevisions from '../../hooks/usePaginatedTrainingRevisions';
import { AppContext } from '../../../App';
import { ITrainingRevision } from '../../../types/my-types';
import { format } from 'date-fns';
import TableButtonsHeader from '../../common/TableButtonsHeader';
import { compactTableStyles } from '../../common/tableStyles';

export interface ITrainingSetRevisionListProps {
  trainingSetId: string;
}

const TrainingSetRevisionList: React.FC<ITrainingSetRevisionListProps> = ({ trainingSetId }) => {
  const { isStaff } = React.useContext(AppContext);
  const [page, setPage] = React.useState(0);
  const [perPage, setPerPage] = React.useState(3);

  const {
    data: trainingRevisions,
    isLoading,
    isError,
    isFetching,
    nextPage,
    previousPage,
  } = usePaginatedTrainingRevisions({
    limit: perPage,
    defaultPage: page,
    trainingSetId,
  });

  if (isError) {
    return <div>Sorry, failed to load training revisions. Please contact the support team.</div>;
  }

  return (
    <>
      <TableButtonsHeader>
        <Typography style={{ fontWeight: 'bold' }}>
          Training Revisions <span style={{ color: '#65BBA9' }}>({trainingRevisions?.count})</span>
        </Typography>
      </TableButtonsHeader>
      <Box sx={compactTableStyles.containerSx}>
        <Table sx={compactTableStyles.tableSx}>
          <TableHead sx={compactTableStyles.tableHeadSx}>
            <TableRow>
              <TableCell align="left">
                Training Revision Name
              </TableCell>
              <TableCell align="center">
                Status
              </TableCell>
              {isStaff ? (
                <TableCell align="center">
                  Core Version
                </TableCell>
              ) : null}
              <TableCell align="center">
                Date Started
              </TableCell>
              <TableCell align="center">
                Last Updated
              </TableCell>

              <TableCell align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody data-cy="training-revision-tablelist-tbody" sx={compactTableStyles.tableBodySx}>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton animation="wave" variant="rounded" height={14} width={[160, 140, 180][i]} sx={{ borderRadius: '4px' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={52} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    {isStaff && <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={48} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>}
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={88} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="center"><Skeleton animation="wave" variant="rounded" height={14} width={88} sx={{ borderRadius: '4px', mx: 'auto' }} /></TableCell>
                    <TableCell align="right"><Skeleton animation="wave" variant="circular" width={18} height={18} sx={{ ml: 'auto' }} /></TableCell>
                  </TableRow>
                ))
              : null}

            {!isLoading && !isFetching && trainingRevisions?.results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                    No training revisions found.
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading &&
              trainingRevisions?.results
                .filter((tr) => !!tr.status)
                .map((tr: ITrainingRevision) => (
                  <TableRow key={tr.id}>
                    <TableCell component="th" scope="row">
                      <DoNotStripSpacesSpan
                        title={tr.label}
                        cypressId={`lbl-training-set-revision-${tr.label}`}
                      />
                    </TableCell>
                    <TableCell align="center" data-cy="training-set-status">
                      {tr.status}
                    </TableCell>
                    {isStaff && <TableCell align="center">{tr.core_version}</TableCell>}
                    <TableCell align="center">
                      {format(new Date(tr.created_at), 'LLL dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell align="center">
                      {format(new Date(tr.updated_at), 'LLL dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell align="right">
                      <TrainingRevisionDelete
                        trainingRevisionId={tr.id}
                        trainingRevisionName={tr.label}
                      />
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        {(trainingRevisions?.count || 0) > 0 && <TablePagination
          rowsPerPageOptions={[3, 5, 10, 25, 50, 150]}
          component="div"
          count={trainingRevisions?.count || 0}
          rowsPerPage={perPage}
          page={page}
          backIconButtonProps={
            {
              'aria-label': 'Previous Page',
              'data-cy': 'btn-prev-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          nextIconButtonProps={
            {
              'aria-label': 'Next Page',
              'data-cy': 'btn-next-page',
            } as IconButtonProps & { 'data-cy': string }
          }
          onPageChange={(e, p) => {
            e?.preventDefault();
            if (p > page) {
              nextPage();
              setPage(p);
            } else {
              previousPage();
              setPage(p);
            }
          }}
          onRowsPerPageChange={(e: any) => {
            e?.preventDefault();
            setPerPage(e.target.value);
          }}
        />}
      </Box>
    </>
  );
};

export default TrainingSetRevisionList;
